import React, { useState } from "react";
import api, { apiErrorMessage } from "../lib/api";
import { useCan } from "../lib/permissions";
import { toast } from "sonner";
import { SlotRescheduleModal } from "./AppointmentSlotPicker";

const empty = { start_datetime: "", end_datetime: "", reason: "", event_type: "other" };
export function DoctorBlockedTab({ doctorId, blocked, onChange, selfService = false }) {
  const manage = useCan("doctors.manage_blocked_slots");
  const cancelPermission = useCan("appointments.cancel");
  const canCancel = selfService || cancelPermission;
  const canReschedule = useCan("appointments.reschedule");
  const [form, setForm] = useState(empty);
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [reschedule, setReschedule] = useState(null);
  const [cancel, setCancel] = useState(null);
  const base = selfService ? "/clinic/doctors-x/me/blocked-slots" : `/clinic/doctors-x/${doctorId}/blocked-slots`;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const payload = () => ({ ...form, start_datetime: new Date(form.start_datetime).toISOString(), end_datetime: new Date(form.end_datetime).toISOString() });
  const fail = (e) => setError(apiErrorMessage(e, "Unable to complete this action. Please refresh and try again."));
  const preview = async () => {
    const r = await api.post(`${base}/preview`, payload());
    setReview(r.data);
    return r.data;
  };
  const inspect = async (e) => {
    e.preventDefault(); setError("");
    if (new Date(form.end_datetime) <= new Date(form.start_datetime) || new Date(form.end_datetime) <= new Date()) {
      setError("End must be after start and in the future."); return;
    }
    setBusy(true);
    try { await preview(); } catch (e) { fail(e); }
    finally { setBusy(false); }
  };
  const save = async (keep) => {
    setBusy(true); setError("");
    try {
      await api.post(base, { ...payload(), keep_existing: keep, acknowledged_appointment_ids: review.appointments.map(a => a.appointment_id) });
      setReview(null); setForm(empty); toast.success("Time blocked"); onChange();
    } catch (e) {
      if (e.response?.data?.detail?.appointments) setReview({ ...review, appointments: e.response.data.detail.appointments });
      fail(e);
    } finally { setBusy(false); }
  };
  const remove = async (id) => {
    setBusy(true); setError("");
    try { await api.delete(`${base}/${id}`); toast.success("Block removed"); onChange(); }
    catch (e) { fail(e); } finally { setBusy(false); }
  };
  const update = (key, value) => { setForm({ ...form, [key]: value }); setReview(null); setError(""); };
  const changeAppointment = async (appointment, action, data) => {
    setBusy(true); setError("");
    try {
      if (selfService && action === "cancel") {
        await api.post(`${base}/cancel-appointment`, { ...payload(), ...data, appointment_id: appointment.appointment_id });
      } else {
        await api.post(`/clinic/appointments-x/${appointment.appointment_id}/${action}`, data);
      }
      setCancel(null); setReschedule(null); toast.success(action === "cancel" ? "Appointment cancelled" : "Appointment rescheduled");
      await preview();
    } catch (e) {
      fail(e);
      // The request may have committed before its response was lost. Refresh
      // affected records instead of inviting a duplicate appointment action.
      setCancel(null); setReschedule(null);
      try { await preview(); } catch (_) { /* Keep the original action error. */ }
    } finally { setBusy(false); }
  };
  return <div className="space-y-4">
    {error && <p role="alert" className="text-red-600 text-sm">{error}</p>}
    {(selfService || manage) && <form onSubmit={inspect} className="mg-card" data-testid="blocked-form">
      <h3 className="font-bold text-mg-navy mb-2">Block a slot or time range</h3>
      <p className="text-sm text-slate-600 mb-3">Enter the start and end of one slot, or a range across dates. Times are in {timezone}.</p>
      <fieldset disabled={busy || !!reschedule || !!cancel} className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="text-sm">Start<input type="datetime-local" className="mg-input block w-full" required value={form.start_datetime} onChange={e => update("start_datetime", e.target.value)} data-testid="blk-start" /></label>
        <label className="text-sm">End<input type="datetime-local" className="mg-input block w-full" required value={form.end_datetime} onChange={e => update("end_datetime", e.target.value)} data-testid="blk-end" /></label>
        <label className="text-sm">Event<select className="mg-input block w-full" value={form.event_type} onChange={e => update("event_type", e.target.value)}>{["surgery", "meeting", "leave", "emergency", "other"].map(t => <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</option>)}</select></label>
        <label className="text-sm">Reason (optional)<input className="mg-input block w-full" maxLength={1000} value={form.reason} onChange={e => update("reason", e.target.value)} data-testid="blk-reason" /></label>
        <button type="submit" className="mg-btn-primary" data-testid="add-blk-btn">{busy ? "Checking…" : "Review block"}</button>
      </fieldset>
    </form>}
    {review && <section className="mg-card space-y-3" aria-label="Review affected appointments">
      <h3 className="font-bold">{review.appointments.length} affected appointments</h3>
      <p className="text-sm">Reschedule or cancel appointments below, or explicitly keep them. The block takes effect when you save it. Appointment changes are saved immediately.</p>
      {review.appointments.map(a => <div key={a.appointment_id} className="border rounded-lg p-3 space-y-2">
        <p>{a.patient_name} — {new Date(a.appointment_datetime).toLocaleString()} ({a.duration_minutes || 30} minutes)</p>
        <p className="text-sm">{a.status}</p>
        {canReschedule && ["scheduled", "confirmed"].includes(a.status?.toLowerCase()) && <button type="button" disabled={busy} className="mg-btn-secondary mr-2" onClick={() => setReschedule(a)}>Reschedule</button>}
        {canCancel && ["scheduled", "confirmed"].includes(a.status?.toLowerCase()) && <button type="button" disabled={busy} className="mg-btn-secondary" onClick={() => setCancel(a)}>Cancel appointment</button>}
      </div>)}
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={busy || !!cancel || !!reschedule} className="mg-btn-primary" onClick={() => save(review.appointments.length > 0)}>{review.appointments.length ? "Keep existing appointments & block new bookings" : "Save block"}</button>
        <button type="button" disabled={busy} className="mg-btn-secondary" onClick={() => setReview(null)}>Discard block</button>
      </div>
    </section>}
    {cancel && <div role="dialog" aria-modal="true" aria-label="Cancel affected appointment" className="mg-card space-y-3">
      <p>Cancel the appointment for {cancel.patient_name}? This change is saved immediately.</p>
      <button type="button" className="mg-btn-secondary mr-2" disabled={busy} onClick={() => setCancel(null)}>Go back</button>
      <button type="button" className="mg-btn-primary" disabled={busy} onClick={() => changeAppointment(cancel, "cancel", { reason: form.reason || `Doctor unavailable: ${form.event_type}` })}>Confirm cancellation</button>
    </div>}
    {reschedule && <SlotRescheduleModal appointment={reschedule} excludedRange={review} busy={busy} onClose={() => !busy && setReschedule(null)} onSubmit={(new_datetime, reason) => changeAppointment(reschedule, "reschedule", { new_datetime, reason: reason || form.reason || `Doctor unavailable: ${form.event_type}` })} />}
    <div className="mg-card overflow-x-auto" data-testid="blocked-table">
      <table className="w-full text-sm"><thead><tr className="text-left"><th>Start</th><th>End</th><th>Event / reason</th><th>Manage</th></tr></thead>
        <tbody>{!(blocked || []).length && <tr><td colSpan={4} className="py-6 text-center">No blocked slots.</td></tr>}
          {(blocked || []).map(b => <tr key={b.block_id} data-testid={`blk-${b.block_id}`} className="border-t">
            <td className="py-3">{new Date(b.start_datetime).toLocaleString()}</td><td>{new Date(b.end_datetime).toLocaleString()}</td><td>{b.event_type || "other"}{b.reason ? ` — ${b.reason}` : ""}</td>
            <td>{(selfService || manage) && <button disabled={busy} onClick={() => remove(b.block_id)} data-testid={`del-blk-${b.block_id}`} className="text-red-600" aria-label="Remove blocked time">Remove</button>}</td>
          </tr>)}
        </tbody></table>
    </div>
  </div>;
}
